// AdminWsListener.jsx
import { useEffect, useRef } from "react";
import { notification } from "antd";
import { API_BASE_URL, WS_ENDPOINT_URL } from "configs/AppConfig";
import { ensureWS, subscribe } from "./wsClient";
import { useAdminNotifications } from "contexts/AdminNotificationsContext";
import { playNotificationSound } from "utils/notificationSound";

export default function AdminWsListener() {
  const { addFromWs } = useAdminNotifications();
  const tokenPollRef = useRef(null);
  const cleanupRef = useRef(null);

  useEffect(() => {
    // Skip WebSocket for public portal routes and kiosk (kiosk has its own WS handling)
    if (window.location.pathname.startsWith('/portal') || window.location.pathname.includes('/kiosk')) {
      return;
    }
    
    function getToken() {
      const raw = localStorage.getItem("auth_token");
      if (!raw) return null;
      return raw.replace(/^Bearer\s+/i, "").replace(/^"|"$/g, "");
    }

    function getWsUrl() {
      const base = (WS_ENDPOINT_URL || API_BASE_URL || "").replace(/\/api\/?$/, "");
      return WS_ENDPOINT_URL
        ? WS_ENDPOINT_URL
        : base.replace(/^http/, "ws") + "/ws/";
    }

    function handleMessage(ev) {
      // ev.data is a JSON string from server
      let msg;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }

      if (msg?.type === "pong") return;

      const { event, payload, type, action, data } = msg || {};
      
      // Debug logging for all incoming events
      if (event) {
        console.log('[AdminWs] Received event:', event, 'payload:', payload);
      }

      // Handle SAB real-time updates
      if (type && ["dashboard", "events", "mainboard", "scrollboard"].includes(type)) {
        // These are handled by individual components via their own WebSocket listeners
        // Show notification for significant updates
        if (action === "create" || action === "update") {
          notification.info({
            message: `${type.charAt(0).toUpperCase() + type.slice(1)} Updated`,
            description: `${type.charAt(0).toUpperCase() + type.slice(1)} has been ${action}d`,
            duration: 3,
            placement: 'topRight',
          });
        }
        return;
      }

      // Handle legacy event format
      if (!event || !payload) return;

      // ==================== KIOSK EVENTS ====================
      // These are the key real-time events from kiosk/iPad check-in system
      
      // Kiosk connection status changed
      if (event === 'kiosk.status') {
        if (payload.action === 'connected') {
          notification.success({
            message: '📱 iPad Kiosk Connected',
            description: payload.locationName 
              ? `Kiosk at "${payload.locationName}" is now online`
              : 'An iPad kiosk has connected',
            duration: 5,
            placement: 'topRight',
          });
        } else if (payload.action === 'disconnected') {
          notification.warning({
            message: '📱 iPad Kiosk Disconnected',
            description: payload.locationName 
              ? `Kiosk at "${payload.locationName}" has disconnected`
              : 'An iPad kiosk has disconnected',
            duration: 6,
            placement: 'topRight',
          });
        }
        // Dispatch a custom event so components can listen
        window.dispatchEvent(new CustomEvent('kiosk-status-change', { 
          detail: payload 
        }));
        return;
      }
      
      // Patient checked in via kiosk
      if (event === 'appointment.checkedin' || event === 'kiosk.patient.checkedin') {
        playNotificationSound();
        notification.success({
          message: '🎉 Patient Checked In!',
          description: `${payload.patientName || 'A patient'} has checked in${payload.serviceName ? ` for ${payload.serviceName}` : ''}`,
          duration: 8,
          placement: 'topRight',
        });
        
        // Browser notification
        if (Notification?.permission === "granted") {
          new Notification('Patient Checked In', {
            body: `${payload.patientName} is ready`,
            icon: '/favicon.ico',
          });
        }
        return;
      }

      // Quick check-in completed
      if (event === 'patient.quickcheckin.completed' || event === 'kiosk.patient.quickcheckin') {
        playNotificationSound();
        notification.success({
          message: payload.isLateCheckin ? '✍️ Previous Visit Signed' : '🎉 Quick Check-In Completed!',
          description: payload.isLateCheckin
            ? `${payload.patientName || 'A patient'} signed for the missed check-in on ${payload.missedAt ? new Date(payload.missedAt).toLocaleDateString() : 'a previous visit'}${payload.remainingMissed > 0 ? ` (${payload.remainingMissed} more pending)` : ''}`
            : `${payload.patientName || 'A patient'} has completed quick check-in${payload.isNewPatient ? ' (New Patient)' : ''}`,
          duration: 8,
          placement: 'topRight',
        });
        
        if (Notification?.permission === "granted") {
          new Notification('Quick Check-In', {
            body: `${payload.patientName} checked in`,
            icon: '/favicon.ico',
          });
        }
        return;
      }

      // Form submitted from kiosk
      if (event === 'form.submitted.kiosk' || event === 'kiosk.form.submitted') {
        playNotificationSound();
        notification.info({
          message: '📋 Form Submitted!',
          description: `${payload.patientName || 'A patient'} submitted "${payload.templateName || 'a form'}"`,
          duration: 6,
          placement: 'topRight',
        });
        
        if (Notification?.permission === "granted") {
          new Notification('Form Submitted', {
            body: `${payload.patientName}: ${payload.templateName}`,
            icon: '/favicon.ico',
          });
        }
        return;
      }

      // Form submitted from patient portal (email link / QR code)
      if (event === 'form.assignment.submitted') {
        playNotificationSound();
        notification.success({
          message: '📋 Form Submitted (Portal)',
          description: `${payload.patientName || 'A patient'} submitted "${payload.templateName || 'a form'}"${payload.allComplete ? ' - All forms complete!' : ''}`,
          duration: 8,
          placement: 'topRight',
        });
        
        if (Notification?.permission === "granted") {
          new Notification('Form Submitted via Portal', {
            body: `${payload.patientName}: ${payload.templateName}${payload.allComplete ? ' ✓ All Complete' : ''}`,
            icon: '/favicon.ico',
          });
        }
        
        // Dispatch custom event for components to refresh
        window.dispatchEvent(new CustomEvent('form-submitted', { detail: payload }));
        return;
      }

      // Form signed from kiosk
      if (event === 'form.signed.kiosk' || event === 'kiosk.form.signed') {
        playNotificationSound();
        notification.info({
          message: '✍️ Form Signed!',
          description: `${payload.patientName || 'A patient'} signed "${payload.templateName || 'a form'}"`,
          duration: 6,
          placement: 'topRight',
        });
        return;
      }

      // Forms assigned notification
      if (event === 'forms.assigned') {
        notification.info({
          message: '📋 Forms Assigned',
          description: `${payload.forms?.length || 1} form(s) assigned to ${payload.patientName}`,
          duration: 5,
          placement: 'topRight',
        });
        return;
      }

      // Empty check-in sent to iPad
      if (event === 'checkin.empty.sent') {
        notification.info({
          message: '📱 Check-In Sent to iPad',
          description: `Empty check-in form sent for ${payload.practitionerName || 'practitioner'}`,
          duration: 4,
          placement: 'topRight',
        });
        return;
      }

      // Appointment status changed
      if (event === 'appointment.status.changed') {
        notification.info({
          message: 'Appointment Updated',
          description: `Status changed to ${payload.newStatus?.replace(/_/g, ' ')}`,
          duration: 4,
          placement: 'topRight',
        });
        return;
      }

      // ==================== CLINIC EVENTS ====================
      // Handle clinic-specific events from backend
      if (event.startsWith("clinic.")) {
        const eventType = event.split(".")[1]; // appointments, patients, etc.
        const eventAction = event.split(".")[2]; // created, updated, deleted
        
        playNotificationSound();
        
        notification.info({
          message: `${eventType.charAt(0).toUpperCase() + eventType.slice(1)} ${eventAction}`,
          description: payload.title || `Clinic ${eventType} has been ${eventAction}`,
          duration: 4,
          placement: 'topRight',
        });
        return;
      }

      // Handle other notification events (generic)
      if (event === "booking.created" || event === "booking.updated") {
        if (!payload.url) return;
        playNotificationSound();
        addFromWs({ ...payload, event });

        if (Notification?.permission === "granted") {
          new Notification(payload.title || "Physio Pro", {
            body: payload.body || "",
          });
        }
      }
    }

    function start() {
      const token = getToken();
      if (!token) return false;

      const wsUrl = getWsUrl();
      console.log("[ws] starting singleton on", wsUrl);

      ensureWS(wsUrl, token);

      const unsub = subscribe(handleMessage);
      return unsub;
    }

    // Try start immediately
    let cleanup = start();
    cleanupRef.current = cleanup || null;

    // If token not ready yet, poll for it for a bit
    if (!cleanup) {
      tokenPollRef.current = setInterval(() => {
        if (cleanupRef.current) return;

        const maybeCleanup = start();
        if (maybeCleanup) {
          cleanupRef.current = maybeCleanup;
          clearInterval(tokenPollRef.current);
          tokenPollRef.current = null;
        }
      }, 1000);
    }

    return () => {
      if (tokenPollRef.current) {
        clearInterval(tokenPollRef.current);
        tokenPollRef.current = null;
      }
      if (typeof cleanupRef.current === "function") {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [addFromWs]);

  return null;
}
